import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '../atoms/Button';
import { PICKER_LOCALE_OPTIONS } from '../../constants/locales';
import type { SellerRef } from './SellerActions';

/**
 * Brand-AI form for one seller (colors, font, logo, tone…). An AI agent will
 * fill the remaining ~90% from lvl1 enrichment context.
 *
 * Rendered as a <dialog> + showModal — NOT a portal div — so it can stack ON
 * TOP of the open seller details <dialog> (portals can't enter the top layer
 * above it; dialogs stack in open order and return focus to the caller).
 */

interface BrandGuidelinesDraft {
  primary_color: string;
  secondary_color: string;
  tertiary_color: string;
  font_family: string;
  logo_url: string;
  tone_of_voice: string;
  preferred_language: string;
  website: string;
  notes: string;
}

const EMPTY_BRAND_DRAFT: BrandGuidelinesDraft = {
  primary_color: '#5260FF',
  secondary_color: '#22D3EE',
  tertiary_color: '#F8FAFC',
  font_family: '',
  logo_url: '',
  tone_of_voice: '',
  preferred_language: '',
  website: '',
  notes: '',
};

const COLOR_FIELDS: Array<{ key: keyof BrandGuidelinesDraft; label: string }> = [
  { key: 'primary_color', label: 'Primary color' },
  { key: 'secondary_color', label: 'Secondary color' },
  { key: 'tertiary_color', label: 'Tertiary color' },
];

const inputClass =
  'w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-gray-600 transition-all focus:border-cyan-300/30 focus:outline-none focus:ring-2 focus:ring-[#5260FF]';

export function BrandGuidelinesModal({
  seller,
  onClose,
  onSaved,
}: {
  seller: SellerRef;
  onClose: () => void;
  onSaved: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState<BrandGuidelinesDraft>(EMPTY_BRAND_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.showModal();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);

  const set = (key: keyof BrandGuidelinesDraft) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setDraft((current) => ({ ...current, [key]: event.target.value }));

  const handleSave = () => {
    // TODO: wire to the brand-guidelines n8n action (AI agent fills ~90%
    // from lvl1 enrichment + these basics). For now the draft is validated
    // and handed to the caller.
    if (!draft.primary_color.trim()) {
      setError('Primary color is required.');
      return;
    }
    setSaving(true);
    onSaved();
    onClose();
  };

  return (
    <dialog
      ref={dialog}
      aria-labelledby="brand-guidelines-title"
      onCancel={onClose}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      className="brand-guidelines"
    >
      <div className="border-b border-white/8 px-6 py-5">
        <div className="inline-flex items-center gap-2 rounded-full border border-violet-300/20 bg-violet-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-violet-200">
          <Icon icon="solar:magic-stick-3-bold-duotone" className="h-3.5 w-3.5" aria-hidden="true" />
          AI Brand Agent
        </div>
        <h2 id="brand-guidelines-title" className="mt-3 text-xl font-black text-white">
          Brand guidelines — {seller.label}
        </h2>
        <p className="mt-1 text-sm text-[#8E95AE]">
          Add the basics. The AI agent will detect and complete ~90% of the brand guidelines from the seller's enrichment and research.
        </p>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
        <div>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#6F7893]">Brand colors</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {COLOR_FIELDS.map(({ key, label }) => (
              <label key={key} className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">{label}</span>
                <span className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
                  <input
                    type="color"
                    value={draft[key]}
                    onChange={set(key)}
                    className="h-7 w-7 shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-0"
                    aria-label={label}
                  />
                  <input
                    type="text"
                    value={draft[key]}
                    onChange={set(key)}
                    className="w-full bg-transparent text-xs font-medium uppercase text-white outline-none"
                  />
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-gray-400">Brand font</span>
            <input value={draft.font_family} onChange={set('font_family')} placeholder="e.g. Inter, Montserrat…" className={inputClass} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-gray-400">Logo URL</span>
            <input value={draft.logo_url} onChange={set('logo_url')} placeholder="https://…/logo.png" className={inputClass} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-gray-400">Website</span>
            <input value={draft.website} onChange={set('website')} placeholder="https://seller.com" className={inputClass} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-gray-400">Preferred language</span>
            <select value={draft.preferred_language} onChange={set('preferred_language')} className="h-[52px] w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm text-white focus:outline-none">
              <option value="">Not set</option>
              {PICKER_LOCALE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold text-gray-400">Tone of voice</span>
          <textarea value={draft.tone_of_voice} onChange={set('tone_of_voice')} rows={3} placeholder="e.g. Professional but approachable; avoids jargon…" className={`${inputClass} resize-y`} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold text-gray-400">Anything else the agent should know</span>
          <textarea value={draft.notes} onChange={set('notes')} rows={2} placeholder="Optional context…" className={`${inputClass} resize-y`} />
        </label>

        {error && (
          <p role="alert" className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">{error}</p>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-white/8 px-6 py-4">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="button" variant="primary" loading={saving} icon={<Icon icon="solar:magic-stick-3-bold-duotone" className="h-4 w-4" aria-hidden="true" />} onClick={handleSave}>
          Generate with AI
        </Button>
      </div>
    </dialog>
  );
}
